import { useEffect, useId, useRef, useState } from 'react';
import './userReferral.css';

const STATUS_LABEL = {
    pending: 'Ожидает проверки', confirmed: 'Подтверждён', rejected: 'Отклонён',
};

function formatDate(value) {
    const date = new Date(value);
    return value && !Number.isNaN(date.getTime()) ? date.toLocaleDateString('ru-RU') : '';
}

function LinkIcon() {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M10 13a5 5 0 007 0l3-3a5 5 0 00-7-7l-2 2M14 11a5 5 0 00-7 0l-3 3a5 5 0 007 7l2-2" />
        </svg>
    );
}

/** Отдельный блок: не меняет поля анкеты и не участвует в её сохранении. */
export default function UserReferralPanel({ token, loadReferral }) {
    const titleId = useId();
    const linkId = useId();
    const listId = useId();
    const inputRef = useRef(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const [message, setMessage] = useState('');
    const [expanded, setExpanded] = useState(false);
    const [visibleCount, setVisibleCount] = useState(5);
    const [sharing, setSharing] = useState(false);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setError(false);
        setData(null);
        setMessage('');
        setExpanded(false);
        setVisibleCount(5);
        (async () => {
            try {
                const result = await loadReferral(token);
                if (!result?.success || !result.link) throw new Error('Invalid referral response');
                if (alive) setData(result);
            } catch {
                if (alive) setError(true);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [token, loadReferral, attempt]);

    const copyLink = async () => {
        if (!data?.link) return;
        try {
            await navigator.clipboard.writeText(data.link);
            setMessage('Ссылка скопирована — отправьте её другу.');
        } catch {
            inputRef.current?.focus();
            inputRef.current?.select();
            setMessage('Выделили ссылку. Скопируйте её через меню устройства или Ctrl+C.');
        }
    };

    const shareLink = async () => {
        if (!data?.link || sharing) return;
        if (typeof navigator.share !== 'function') {
            await copyLink();
            return;
        }
        setSharing(true);
        try {
            await navigator.share({
                title: 'Приглашение в Lovirds',
                text: 'Присоединяйся к Lovirds по моей ссылке!',
                url: data.link,
            });
        } catch (shareError) {
            if (shareError?.name !== 'AbortError') await copyLink();
        } finally {
            setSharing(false);
        }
    };

    const referrals = Array.isArray(data?.referrals) ? data.referrals : [];
    const invitedBy = data?.invited_by;
    const total = data?.total ?? referrals.length;

    return (
        <section className="lv-card lv-referral" aria-labelledby={titleId} aria-busy={loading}>
            <div className="lv-referral__heading">
                <span className="lv-referral__icon"><LinkIcon /></span>
                <h2 id={titleId} className="lv-card__title">Пригласить друзей</h2>
            </div>
            <p className="lv-referral__text">
                Поделитесь личной ссылкой. После создания анкеты друг появится в вашем списке.
            </p>

            {loading ? <p className="lv-referral__muted" role="status">Загружаем вашу ссылку…</p> : null}
            {error ? (
                <div className="lv-referral__error" role="alert">
                    <p>Не удалось загрузить приглашения.</p>
                    <button type="button" className="lv-referral__button" onClick={() => setAttempt((n) => n + 1)}>
                        Повторить
                    </button>
                </div>
            ) : null}

            {!loading && data ? (
                <>
                    <label className="lv-referral__label" htmlFor={linkId}>Ваша ссылка</label>
                    <input ref={inputRef} id={linkId} className="lv-referral__link" type="text"
                           readOnly value={data.link} onFocus={(event) => event.target.select()} />
                    <div className="lv-referral__actions">
                        <button type="button" className="lv-referral__button lv-referral__button--primary" onClick={copyLink}>
                            Копировать
                        </button>
                        <button type="button" className="lv-referral__button" onClick={shareLink} disabled={sharing}>
                            Поделиться
                        </button>
                    </div>
                    {message ? <p className="lv-referral__feedback" role="status">{message}</p> : null}

                    {data.invited_by !== undefined ? (
                        <div className="lv-referral__inviter">
                            <span className="lv-referral__muted">Кто вас пригласил</span>
                            <span>{invitedBy ? invitedBy.name : 'Приглашение не зафиксировано'}</span>
                        </div>
                    ) : null}

                    <button type="button" className="lv-referral__toggle" aria-expanded={expanded}
                            aria-controls={listId} onClick={() => setExpanded((value) => !value)}>
                        <span>Мои приглашённые <b className="lv-referral__count">{total}</b></span>
                        <span aria-hidden="true">{expanded ? '−' : '+'}</span>
                    </button>
                    <div id={listId} hidden={!expanded}>
                        {referrals.length ? (
                            <ul className="lv-referral__list">
                                {referrals.slice(0, visibleCount).map((item) => (
                                    <li key={item.id} className="lv-referral__person">
                                        <div className="lv-referral__person-info">
                                            <strong>{item.referred_name || 'Пользователь'}</strong>
                                            <span className="lv-referral__muted">{formatDate(item.date_create)}</span>
                                        </div>
                                        <span className={`lv-referral__status lv-referral__status--${
                                            Object.prototype.hasOwnProperty.call(STATUS_LABEL, item.status) ? item.status : 'pending'
                                        }`}>
                                            {STATUS_LABEL[item.status] || 'Ожидает проверки'}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        ) : <p className="lv-referral__muted">Пока никого. Отправьте свою ссылку другу.</p>}
                        {referrals.length > visibleCount ? (
                            <button type="button" className="lv-referral__button lv-referral__more"
                                    onClick={() => setVisibleCount((count) => count + 5)}>
                                Показать ещё
                            </button>
                        ) : null}
                    </div>
                    <p className="lv-referral__note">В списке учитываются созданные анкеты, а не переходы по ссылке.</p>
                </>
            ) : null}
        </section>
    );
}
